import { useEffect, useRef } from 'react';
import { runDailyStats, telemetry } from '@core/telemetry';
import { useData } from './data/repositories';

/**
 * Суточная задача статистики. Ничего не рисует.
 *
 * В браузере нет ни `WorkManager`, ни фоновой синхронизации Safari — единственный
 * момент, когда наш код точно выполняется, это открытое приложение. Поэтому задача
 * запускается при запуске и при возвращении к приложению, а не по расписанию;
 * не чаще раза в час — об этом заботится сама задача.
 */
export function DailyStats() {
  const { db, files } = useData();
  const hiddenAt = useRef(0);

  useEffect(() => {
    const run = () => void runDailyStats(db, files, __APP_VERSION__);

    /*
     * Не сразу, а через несколько секунд после запуска.
     *
     * Срез состояния — это запрос по делам, событиям, клиентам и документам каждого
     * дела, то есть десятки запросов к той же базе, из которой сейчас рисуется первый
     * экран. Скорость открытия дела и есть продукт (NFR.md), и уступать её статистике
     * нельзя. Ждать нечего: суточный агрегат не протухает за пять секунд.
     */
    const startup = setTimeout(run, START_DELAY_MS);

    const onVisibility = () => {
      if (document.visibilityState !== 'visible') {
        hiddenAt.current = Date.now();
        return;
      }

      /*
       * Возвращение после долгого перерыва считаем запуском.
       *
       * На iPhone приложение с домашнего экрана не перезагружает страницу днями:
       * его сворачивают и разворачивают. Считать запуском только загрузку страницы
       * значило бы не увидеть, что человек открывал приложение каждый день недели, —
       * а «возвращается ли» и есть главный вопрос (docs/TELEMETRY.md). Полчаса —
       * граница между «отвлёкся на звонок» и «пришёл в другой раз».
       */
      if (hiddenAt.current > 0 && Date.now() - hiddenAt.current > RETURN_GAP_MS) {
        telemetry.record('appOpened');
      }
      run();
    };

    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearTimeout(startup);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [db, files]);

  return null;
}

const START_DELAY_MS = 5000;
const RETURN_GAP_MS = 30 * 60 * 1000;
